import * as THREE from 'three';

export type RendererType = 'webgpu' | 'webgl2' | 'webgl1';
export type GPUTier = 'high' | 'mid' | 'low';

export interface RendererResult {
    renderer: THREE.WebGLRenderer;
    type: RendererType;
    gpuTier: GPUTier;
    capabilities: {
        maxTextureSize: number;
        maxDrawBuffers: number;
        floatTextures: boolean;
        instancedRendering: boolean;
        multiDraw: boolean;
        computeShaders: boolean;
    };
}

export interface RendererOptions {
    canvas: HTMLCanvasElement;
    antialias?: boolean;
    alpha?: boolean;
    preferWebGPU?: boolean;
    pixelRatio?: number;
}

export class FikoRendererFactory {
    static async createBestRenderer(options: RendererOptions): Promise<RendererResult> {
        const antialias = options.antialias ?? true;
        const alpha = options.alpha ?? false;
        const preferWebGPU = options.preferWebGPU ?? true;
        const pixelRatio = options.pixelRatio ?? window.devicePixelRatio;

        if (preferWebGPU && await this.isWebGPUAvailable()) {
            try { return await this.createWebGPURenderer(options.canvas, antialias, alpha, pixelRatio); }
            catch (e) { console.warn('[RendererFactory] WebGPU failed, falling back:', e); }
        }

        try { return this.createWebGL2Renderer(options.canvas, antialias, alpha, pixelRatio); }
        catch (e) { return this.createWebGL1Renderer(options.canvas, antialias, alpha, pixelRatio); }
    }

    static async isWebGPUAvailable(): Promise<boolean> {
        if (!navigator.gpu) return false;
        try { return (await navigator.gpu.requestAdapter()) !== null; }
        catch { return false; }
    }

    static detectGPUTier(gl: WebGLRenderingContext | WebGL2RenderingContext): GPUTier {
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) return 'mid';
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
        const highPatterns = [/nvidia.*rtx/i, /nvidia.*gtx.*[1-3]\d{3}/i, /amd.*rx.*[67]\d{3}/i, /apple.*m[1-9]/i, /quadro/i];
        const lowPatterns = [/intel.*hd/i, /intel.*uhd.*6[0-2]\d/i, /mali/i, /adreno.*[1-5]\d{2}/i, /powervr/i];
        for (const p of highPatterns) if (p.test(renderer)) return 'high';
        for (const p of lowPatterns) if (p.test(renderer)) return 'low';
        return 'mid';
    }

    private static async createWebGPURenderer(canvas: HTMLCanvasElement, antialias: boolean, alpha: boolean, pixelRatio: number): Promise<RendererResult> {
        // @ts-ignore - WebGPU renderer is optional, dynamic import will throw if unavailable
        const { default: WebGPURenderer } = await import(/* webpackIgnore: true */ 'three/src/renderers/webgpu/WebGPURenderer.js');
        const renderer = new WebGPURenderer({ canvas, antialias, alpha, powerPreference: 'high-performance' });
        renderer.setPixelRatio(pixelRatio);
        return {
            renderer: renderer as any, type: 'webgpu', gpuTier: 'high',
            capabilities: { maxTextureSize: 8192, maxDrawBuffers: 8, floatTextures: true, instancedRendering: true, multiDraw: true, computeShaders: true }
        };
    }

    private static createWebGL2Renderer(canvas: HTMLCanvasElement, antialias: boolean, alpha: boolean, pixelRatio: number): RendererResult {
        const context = canvas.getContext('webgl2', { antialias, alpha, powerPreference: 'high-performance', stencil: false, depth: true });
        if (!context) throw new Error('WebGL2 context creation failed');
        const renderer = new THREE.WebGLRenderer({ canvas, context, antialias, alpha, powerPreference: 'high-performance', stencil: false, depth: true });
        renderer.setPixelRatio(pixelRatio);
        const gpuTier = this.detectGPUTier(context);
        this.optimizeRendererForTier(renderer, gpuTier);
        return {
            renderer, type: 'webgl2', gpuTier,
            capabilities: {
                maxTextureSize: context.getParameter(context.MAX_TEXTURE_SIZE),
                maxDrawBuffers: context.getParameter(context.MAX_DRAW_BUFFERS),
                floatTextures: context.getExtension('EXT_color_buffer_float') !== null,
                instancedRendering: true, multiDraw: context.getExtension('WEBGL_multi_draw') !== null, computeShaders: false
            }
        };
    }

    private static createWebGL1Renderer(canvas: HTMLCanvasElement, antialias: boolean, alpha: boolean, pixelRatio: number): RendererResult {
        const context = canvas.getContext('webgl', { antialias, alpha, powerPreference: 'high-performance', stencil: false, depth: true });
        if (!context) throw new Error('WebGL1 context creation failed');
        const renderer = new THREE.WebGLRenderer({ canvas, context, antialias, alpha, powerPreference: 'high-performance', stencil: false, depth: true });
        renderer.setPixelRatio(pixelRatio);
        return {
            renderer, type: 'webgl1', gpuTier: this.detectGPUTier(context),
            capabilities: { maxTextureSize: context.getParameter(context.MAX_TEXTURE_SIZE), maxDrawBuffers: 1,
                floatTextures: context.getExtension('OES_texture_float') !== null,
                instancedRendering: context.getExtension('ANGLE_instanced_arrays') !== null, multiDraw: false, computeShaders: false }
        };
    }

    private static optimizeRendererForTier(renderer: THREE.WebGLRenderer, tier: GPUTier) {
        switch (tier) {
            case 'high':
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                break;
            case 'mid':
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFShadowMap;
                break;
            case 'low':
                renderer.shadowMap.enabled = false;
                break;
        }
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
    }
}
